/**
 * Client-side mirror of `synthesis-engine.deriveEventFamily`. Used by the
 * chat surfaces to send `event_family` to /api/suggestions/rotate so the
 * public endpoint can drop family-mismatched gallery rows (V2.6.1 — Brian's
 * V2.6 live-smoke C1 fix).
 *
 * Pattern order MUST match the backend exactly — duplicating this keeps
 * the public endpoint independent of an event_id round-trip; if a typo
 * drifts the two copies the worst case is the public rotate stops
 * filtering for that family (graceful degrade).
 */

export const VALID_EVENT_FAMILIES = [
  'any',
  'cherry',
  'nos',
  'cow-palace',
  'santa-clara',
  'alameda',
  'fresno',
  'indio',
  'maricopa',
  'chandler',
  'riverside',
  'other',
] as const

export type EventFamily = (typeof VALID_EVENT_FAMILIES)[number]

const FAMILY_NAME_PATTERNS: ReadonlyArray<readonly [EventFamily, RegExp]> = [
  ['cow-palace',  /\bcow\s*palace\b/i],
  ['santa-clara', /\bsanta\s*clara\b/i],
  ['nos',         /\b(national\s+orange\s+show|orange\s+show|NOS)\b/i],
  ['cherry',      /\b(beaumont\s+cherry|cherry)\b/i],
  ['indio',       /\bindio\b/i],
  ['alameda',     /\balameda\b/i],
  ['fresno',      /\bfresno\b/i],
  ['maricopa',    /\bmaricopa\b/i],
  ['chandler',    /\bchandler\b/i],
  ['riverside',   /\briverside\b/i],
]

/**
 * Returns the family enum string. 'any' on missing/empty input (caller
 * sends nothing). 'other' on a non-empty unrecognized name.
 */
export function deriveEventFamily(eventName?: string | null): EventFamily {
  if (!eventName || typeof eventName !== 'string') return 'any'
  const name = eventName.trim()
  if (!name) return 'any'
  for (const [family, pat] of FAMILY_NAME_PATTERNS) {
    if (pat.test(name)) return family
  }
  return 'other'
}
